import { useCallback, useEffect, useRef, useState } from "react";
import type { Route } from "./route";
import { matchRoute, routeToPath } from "./route";

/**
 * 不明な URL を `/`（門）へ書き換えて採用する（N-005: 404 画面を作らず
 * `replaceState` で `/` へ。戻る操作で不明 URL に再突入させないため push ではなく replace）。
 * D-119 で `/` の主は稽古の段から門（S-0）へ移ったが、行き先が `/` である点は変わらない。
 */
function resolveInitialRoute(): Route {
  const matched = matchRoute(window.location.pathname);
  if (matched) {
    return matched;
  }
  window.history.replaceState(null, "", "/");
  return { name: "title" };
}

export interface UseRouterResult {
  readonly route: Route;
  readonly navigate: (route: Route) => void;
  /**
   * ブラウザの戻る（`popstate`）で段（S-2）から抜けた回数。増えるたびに「中断した」ことを
   * 意味する（screen-flow.md §6.2・N-004）。真偽値ではなく回数にしているのは、
   * 2回連続で中断が起きても購読側のuseEffectが確実に発火するようにするため。
   *
   * この判定を段の画面（`LevelScreen`）自身に持たせず、常にマウントされ続けるこのフックに
   * 持たせている理由: 段の画面はまさにこの`popstate`が発火した瞬間にアンマウントされる
   * （ルートが変わるため）。段の画面が自前で`popstate`を購読すると、useRouter側の
   * 購読が先に走ってルートを更新 → 段の画面がアンマウント（＝自分の購読を解除）
   * という順序になった場合に、段の画面側のハンドラがブラウザのディスパッチ列から
   * 取り除かれて発火しないことがある（不安定な競合）。
   */
  readonly interruptedFromLevelCount: number;
}

/**
 * 最小ルータの React フック結線。`pushState` / `popstate` の購読だけの薄い実装で、
 * ライブラリを持ち込まない（spec/design.md「画面遷移とルーティング設計」）。
 */
export function useRouter(): UseRouterResult {
  const [route, setRoute] = useState<Route>(resolveInitialRoute);
  const [interruptedFromLevelCount, setInterruptedFromLevelCount] = useState(0);
  const routeRef = useRef(route);
  routeRef.current = route;

  useEffect(() => {
    const handlePopState = () => {
      const previous = routeRef.current;
      const matched = matchRoute(window.location.pathname);
      const next = matched ?? { name: "title" as const };
      if (!matched) {
        window.history.replaceState(null, "", "/");
      }
      if (previous.name === "level" && next.name !== "level") {
        setInterruptedFromLevelCount((count) => count + 1);
      }
      setRoute(next);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = useCallback((next: Route) => {
    window.history.pushState(null, "", routeToPath(next));
    setRoute(next);
  }, []);

  return { route, navigate, interruptedFromLevelCount };
}
